import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ScrollText, Clock, CheckCircle2, AlertTriangle, XCircle, Sparkles, Mail,
  Truck, CalendarClock, FileText, Loader2, Info, Copy, Shield, Users,
} from "lucide-react";
import { toast } from "sonner";

/**
 * /shared/case/:token — Public, read-only view of a case.
 * NO login required. Uses a raw axios call (no interceptors) so we don't
 * accidentally hit the auth-refresh flow with someone else's session.
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function shareErrorMessage(e) {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  if (status === 410) return detail || "This link has expired or been revoked.";
  if (status === 404) return "We couldn't find this link. Ask the case owner for a fresh one.";
  return "Something went wrong loading this snapshot.";
}

function issueDotColor(severity) {
  if (severity === "critical") return "#991B1B";
  if (severity === "minor") return "#065F46";
  return "#92400E";
}

const LIFECYCLE_LABEL = {
  in_preparation:       "In preparation",
  filed_with_uscis:     "Filed with USCIS",
  receipted:            "Receipted by USCIS",
  biometrics_scheduled: "Biometrics scheduled",
  interview_scheduled:  "Interview scheduled",
  rfe_pending:          "RFE — response due",
  noid_pending:         "NOID — response due",
  approved:             "Approved",
  denied:               "Denied",
};

const LIFECYCLE_PILL = {
  in_preparation:       "pill-ai",
  filed_with_uscis:     "pill-warn",
  receipted:            "pill-ok",
  biometrics_scheduled: "pill-ai",
  interview_scheduled:  "pill-ai",
  rfe_pending:          "pill-warn",
  noid_pending:         "pill-bad",
  approved:             "pill-ok",
  denied:               "pill-bad",
};

const KIND_ICON = {
  case_created:      Clock,
  case_filed:        Truck,
  notice_receipt:    CheckCircle2,
  notice_biometrics: CalendarClock,
  notice_interview:  CalendarClock,
  notice_rfe:        AlertTriangle,
  notice_noid:       AlertTriangle,
  notice_approval:   CheckCircle2,
  notice_denial:     XCircle,
  notice_other:      FileText,
  draft_generated:   Sparkles,
  response_mailed:   Mail,
};

const NOTICE_TYPE_LABEL = {
  receipt: "Receipt Notice",
  rfe: "Request for Evidence (RFE)",
  noid: "Notice of Intent to Deny (NOID)",
  approval: "Approval Notice",
  denial: "Denial",
  biometrics: "Biometrics Appointment",
  interview: "Interview Notice",
  transfer: "Case Transfer",
  reopening: "Reopening",
  withdrawal: "Withdrawal",
  fee_receipt: "Fee Receipt",
  other: "Other Notice",
};

const NOTICE_PILL = {
  receipt: "pill-ok", approval: "pill-ok", denial: "pill-bad", noid: "pill-bad",
  rfe: "pill-warn", biometrics: "pill-ai", interview: "pill-ai",
};

export default function SharedCasePage() {
  const { token } = useParams();
  const [state, setState] = useState({ phase: "loading" });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/api/shared/case/${token}`)
      .then((r) => { if (!cancelled) setState({ phase: "ok", data: r.data }); })
      .catch((e) => {
        if (cancelled) return;
        setState({ phase: "error", msg: shareErrorMessage(e) });
      });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-app)" }}>
      <SharedTopBar />
      {state.phase === "loading" && (
        <div className="mx-auto max-w-6xl px-6 py-16 flex items-center gap-2 text-sm"
             style={{ color: "var(--text-secondary)" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading case snapshot…
        </div>
      )}
      {state.phase === "error" && <ErrorBlock msg={state.msg} />}
      {state.phase === "ok" && <SharedCaseBody data={state.data} shareToken={token} />}
    </div>
  );
}

function SharedTopBar() {
  return (
    <header className="sticky top-0 z-30 backdrop-blur-md border-b"
            style={{ borderColor: "var(--border-stone)", background: "rgba(249,248,245,0.90)" }}>
      <div className="mx-auto max-w-7xl px-6 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="shared-logo">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center"
               style={{ background: "var(--action)" }}>
            <ScrollText className="w-4 h-4 text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-[16px]" style={{ color: "var(--text-primary)" }}>
              DS-260USA<span style={{ color: "var(--action)" }}>.com</span>
            </div>
            <div className="text-[10.5px] uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
              Shared Case Snapshot
            </div>
          </div>
        </Link>
        <Link to="/" className="btn-ghost text-sm" data-testid="shared-back-link">
          About DS-260USA.com
        </Link>
      </div>
    </header>
  );
}

function ErrorBlock({ msg }) {
  return (
    <div className="mx-auto max-w-lg px-6 py-16 text-center" data-testid="shared-error">
      <XCircle className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--text-secondary)" }} />
      <h1 className="font-display text-2xl font-bold mb-2">Snapshot unavailable</h1>
      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{msg}</p>
    </div>
  );
}

function SharedCaseBody({ data, shareToken }) {
  const { share, case: c, timeline, notices } = data;
  const dl = daysLeft(share.expires_at);
  return (
    <div className="mx-auto max-w-5xl px-6 py-10" data-testid="shared-case-page">
      <ReadOnlyBanner dl={dl} note={share.note} />

      <div className="card-surface p-6 mb-5" data-testid="shared-case-header">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="pill pill-ai mb-2 inline-flex">Shared snapshot</div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold mb-1"
                style={{ color: "var(--text-primary)" }}>
              {c.case_type}
            </h1>
            <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
              {c.petitioner_name && <>Petitioner: <strong>{c.petitioner_name}</strong> · </>}
              {c.beneficiary_name && <>Beneficiary: <strong>{c.beneficiary_name}</strong></>}
              {c.children_count > 0 && ` · +${c.children_count} child${c.children_count === 1 ? "" : "ren"}`}
            </div>
            {c.filed_at && (
              <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
                Mailed to USCIS on {formatDate(c.filed_at)}
                {c.filing_mailed_via && ` via ${c.filing_mailed_via}`}
                {c.filing_tracking_number && ` · Tracking: ${c.filing_tracking_number}`}
              </div>
            )}
          </div>
          <span className={`pill ${LIFECYCLE_PILL[c.lifecycle_status] || "pill-ai"}`}
                data-testid="shared-lifecycle-pill">
            {LIFECYCLE_LABEL[c.lifecycle_status] || "In preparation"}
          </span>
        </div>
      </div>

      <TimelineSection events={timeline} />
      <NoticesSection notices={notices} />

      <div className="mt-8 text-[11px] italic text-center" style={{ color: "var(--text-secondary)" }}>
        This snapshot was generated by DS-260USA.com on behalf of the case owner. The AI-drafted
        response letters shown below are drafts only and are not legal advice.
      </div>
    </div>
  );
}

function ReadOnlyBanner({ dl, note }) {
  return (
    <div
      className="mb-5 rounded-lg p-4 flex items-start gap-3"
      style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-border)",
               color: "var(--ai-text)" }}
      data-testid="shared-readonly-banner"
    >
      <Shield className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <div className="text-[13px] leading-relaxed flex-1">
        <div className="font-bold">Read-only shared view</div>
        <div>
          You&apos;re viewing a case snapshot the owner shared with you. Nothing can be edited from this
          page.
          {dl != null && dl > 0 && <> This link expires in <strong>{dl} day{dl === 1 ? "" : "s"}</strong>.</>}
        </div>
        {note && (
          <div className="mt-2 text-[12px] italic">
            &ldquo;{note}&rdquo; — from the case owner
          </div>
        )}
      </div>
    </div>
  );
}

function TimelineSection({ events }) {
  if (!events || events.length === 0) return null;
  return (
    <section className="card-surface p-6 mb-5" data-testid="shared-timeline">
      <h2 className="font-display font-bold text-lg mb-4" style={{ color: "var(--text-primary)" }}>
        Case timeline
      </h2>
      <ol className="relative pl-6 space-y-4">
        <div aria-hidden className="absolute left-[9px] top-1 bottom-1 w-[2px]"
             style={{ background: "var(--border-stone)" }} />
        {events.map((ev) => {
          const Icon = KIND_ICON[ev.kind] || Clock;
          const deadline = ev.meta?.response_deadline;
          const dl = deadline ? daysBetween(new Date(), new Date(deadline)) : null;
          return (
            <li key={ev.id} className="relative pl-2" data-testid={`shared-event-${ev.id}`}>
              <span className="absolute -left-[26px] top-0.5 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ background: "#FFFFFF", border: "1px solid var(--border-stone)" }}>
                <Icon className="w-3 h-3" style={{ color: "var(--text-primary)" }} />
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-display font-bold text-[13px]" style={{ color: "var(--text-primary)" }}>
                  {ev.label}
                </span>
                {ev.occurred_at && (
                  <span className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
                    {formatDate(ev.occurred_at)}
                  </span>
                )}
                {ev.meta?.uscis_receipt_number && (
                  <span className="pill" style={{ background: "#F3F4F6", color: "#374151" }}>
                    <FileText className="w-3 h-3" /> {ev.meta.uscis_receipt_number}
                  </span>
                )}
                {deadline && dl != null && (() => {
                  let pillClass = "pill-ok";
                  if (dl <= 14) pillClass = "pill-bad";
                  else if (dl <= 30) pillClass = "pill-warn";
                  return (
                    <span className={`pill ${pillClass}`}>
                      <CalendarClock className="w-3 h-3" />
                      {dl >= 0 ? `${dl} day${dl === 1 ? "" : "s"} to respond` : `Overdue by ${-dl}`}
                    </span>
                  );
                })()}
              </div>
              {ev.note && (
                <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{ev.note}</div>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function NoticesSection({ notices }) {
  if (!notices || notices.length === 0) return null;
  return (
    <section className="mb-5" data-testid="shared-notices">
      <h2 className="font-display font-bold text-lg mb-3" style={{ color: "var(--text-primary)" }}>
        USCIS notices &amp; AI drafts
      </h2>
      <div className="space-y-4">
        {notices.map((n) => <NoticeBlock key={n.id} notice={n} />)}
      </div>
    </section>
  );
}

function NoticeBlock({ notice }) {
  const e = notice.extracted || {};
  const t = (e.notice_type || "other").toLowerCase();
  const pill = NOTICE_PILL[t] || "pill-ai";
  return (
    <div className="card-surface p-5" data-testid={`shared-notice-${notice.id}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className={`pill ${pill}`}>{NOTICE_TYPE_LABEL[t] || "USCIS Notice"}</span>
        {e.uscis_receipt_number && (
          <span className="pill" style={{ background: "#F3F4F6", color: "#374151" }}>
            <FileText className="w-3 h-3" /> Ends in {e.uscis_receipt_number}
          </span>
        )}
        {e.form_type && (
          <span className="pill" style={{ background: "#EEF2FF", color: "#3730A3" }}>{e.form_type}</span>
        )}
        {e.response_deadline && (
          <span className="pill pill-warn">
            <CalendarClock className="w-3 h-3" /> Reply by {formatDate(e.response_deadline)}
          </span>
        )}
      </div>
      {e.summary && (
        <div className="text-sm mb-2" style={{ color: "var(--text-primary)" }}>{e.summary}</div>
      )}
      {e.issues && e.issues.length > 0 && (
        <div className="mt-3">
          <div className="text-[10.5px] uppercase tracking-wider mb-2"
               style={{ color: "var(--text-secondary)" }}>Issues raised</div>
          <ul className="space-y-2">
            {e.issues.map((iss, i) => (
              <li key={`${iss.topic || "issue"}-${i}`} className="text-xs flex items-start gap-2" style={{ color: "var(--text-primary)" }}>
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5"
                      style={{ background: issueDotColor(iss.severity) }} />
                <div>
                  <div className="font-semibold">{iss.topic}</div>
                  <div style={{ color: "var(--text-secondary)" }}>{iss.requested}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {notice.draft_response && (
        <div className="mt-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-[10.5px] uppercase tracking-wider"
                 style={{ color: "var(--text-secondary)" }}>
              AI-drafted response letter
            </div>
            <button type="button" onClick={() => copyDraft(notice.draft_response)}
                    className="btn-ghost text-xs">
              <Copy className="w-3.5 h-3.5 inline mr-1" /> Copy draft
            </button>
          </div>
          <div className="rounded-md p-4 max-h-[420px] overflow-auto whitespace-pre-wrap font-mono text-[12px] leading-relaxed"
               style={{ background: "#FFFBF3", border: "1px solid var(--warn-border)",
                        color: "var(--text-primary)" }}
               data-testid="shared-notice-draft">
            {notice.draft_response}
          </div>
        </div>
      )}
    </div>
  );
}

function copyDraft(text) {
  try { navigator.clipboard.writeText(text); toast.success("Draft copied."); }
  catch { toast.error("Copy failed — select the text manually."); }
}

function formatDate(iso) {
  if (!iso) return "";
  const [y, m, d] = (iso || "").split("-");
  if (!y || !m || !d) return iso;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
}
function daysBetween(from, to) {
  if (!(to instanceof Date) || isNaN(to)) return null;
  return Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
}
function daysLeft(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}
